// @flow
import 'bootstrap/dist/css/bootstrap.min.css';
import "./styles/style.scss";
import "./styles/dashboard.scss";
import "./styles/toggle.scss";
import "./styles/ticketing.scss";
import "./styles/grid_layout.scss";
import "@fortawesome/fontawesome-free/css/all.min.css";

import React from "react";
import ReactDOM from "react-dom";
import { BrowserRouter as Router, Route } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import { Provider } from "react-redux";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { initAnonymization } from "./lib/Anonymization";

import { initAuth } from "./lib/Auth";

import URLHandler from "./components/URLHandler.tsx";
import AppErrorBoundary from "./components/AppErrorBoundary.tsx";
import { IntlProviderWrapper } from "./components/IntlProviderWrapper";
import registerServiceWorker from "./registerServiceWorker";
import TooltipProvider from "./components/TooltipProvider.tsx";

// initialize backend
import { combineReducers, createStore } from "redux";
import { setBackend } from "./lib/Backend";
// eslint-disable-next-line
import { RestBackend } from "./lib/Backend.rest";
import { clearFeatureMatrix, initBusinessLogic } from "./lib/BusinessLogic";
import { ensureDeviceUUID, getBrowser } from "./lib/Util";
import { subscribe, MSG_TYPES } from "./lib/PubSub";
import { reducers } from "./components/redux/reducers";
import { initPerformanceLogging } from "./lib/PerformanceLogging";
import local_settings from "./lib/LocalSettings";

export const store = createStore(combineReducers(reducers));

const REACT_VERSION = React.version;
const BROWSER = getBrowser();

registerLocalSettingsListeners();
registerResizeObserverPatch();

const ErrorBoundaryFallback = ({ resetError }: { resetError: () => void }) => (
    <div className="p-4 text-center">
        <h1>
            <FormattedMessage id={"common.something_went_wrong"} defaultMessage={"Something went wrong"} />
        </h1>
        <button className="btn btn-primary" onClick={resetError}>
            <FormattedMessage id={"common.retry"} defaultMessage={"Retry"} />
        </button>
    </div>
);

/** Prevent ResizeObserver loop errors (e.g. during drag) by deferring callbacks to next frame
 * The full error message: "ResizeObserver loop completed with undelivered notifications."
 *
 * The error is caused by the ResizeObserver callback being called too often, which can cause the browser to crash.
 * This patch defers the callback to the next frame, which prevents the error.
 *
 * This error was caused by scrolling the container during an item drag operation in LineLayoutEditor.tsx.
*/
function registerResizeObserverPatch() {
    if (typeof window.ResizeObserver !== "undefined") {
        const NativeResizeObserver = window.ResizeObserver;
        window.ResizeObserver = class ResizeObserverPatched extends NativeResizeObserver {
            constructor(callback) {
                super((entries, observer) => {
                    window.requestAnimationFrame(() => {
                        callback(entries, observer);
                    });
                });
            }
        };
    }
}

/** Ensure that the local settings are saved when the user navigates away from the app */
function registerLocalSettingsListeners() {
    window.addEventListener("beforeunload", local_settings.saveAll.bind(local_settings));
    // Ensures user session data is saved when opening the app in a new tab right after logging in elsewhere.
    window.addEventListener("blur", local_settings.saveAll.bind(local_settings));
    // Ensures user session data is saved before opening the app in a new tab by clicking on a link - right after logging in.
    window.addEventListener(
        "mousedown",
        event => event.target.closest && event.target.closest("a") && local_settings.saveAll()
    );
}

async function main() {
    // initialization of main components
    ensureDeviceUUID();
    clearFeatureMatrix();
    const backend = new RestBackend();
    setBackend(backend);
    await initAuth();
    await initAnonymization(backend.getAxiosInstance());
    await initBusinessLogic();
    await initPerformanceLogging();

    subscribe(MSG_TYPES.redirect_to_login, async () => {
        if (window.location.pathname !== "/login") {
            window.location = "/login";
        }
    });

    const queryClient = new QueryClient();
    if (process.env.NODE_ENV === "development") {
        // Needed for the DevTools Browser Extension to work
        window.__TANSTACK_QUERY_CLIENT__ = queryClient;
    }

    // initialize document
    let root = document.getElementById("root");
    console.log("React Version: " + REACT_VERSION);
    console.log("Browser: " + BROWSER);

    if (root) {
        if (BROWSER !== "msie") {
            ReactDOM.render(
                <IntlProviderWrapper>
                    <QueryClientProvider client={queryClient}>
                        <Provider store={store}>
                            <TooltipProvider>
                                <AppErrorBoundary fallbackUI={ErrorBoundaryFallback}>
                                    <Router>
                                        <Route path="/" component={URLHandler} />
                                    </Router>
                                </AppErrorBoundary>
                            </TooltipProvider>
                        </Provider>
                    </QueryClientProvider>
                </IntlProviderWrapper>,
                root
            );
        } else {
            ReactDOM.render(
                <IntlProviderWrapper>
                    <div className="center">
                        <h3>
                            <FormattedMessage
                                id="index.no_msie"
                                defaultMessage="Internet Explorer is not supported. Please use Chrome."
                            />
                        </h3>
                    </div>
                </IntlProviderWrapper>,
                root
            );
        }
    }
    registerServiceWorker();
}

main().catch(err => console.log);
